/**
 * One-fund page types. Data comes from the internal gateway's
 * /internal/etf/:symbol and /internal/etf/:symbol/holdings through our own
 * server (the key never reaches the browser).
 * Field semantics: ETF integration handover, Part A §4.1, §4.2, §12.
 */
import type { EtfRow } from "./etf-screener.type";

/** The four index rulebooks; the fund screen (AAOIFI) lives in `status`. */
export const ETF_INDEX_RULEBOOKS = ["djim", "ftse", "msci", "sp"] as const;
export type EtfIndexRulebook = (typeof ETF_INDEX_RULEBOOKS)[number];

/** Constituents per page on the fund page. The gateway allows up to 500. */
export const ETF_HOLDINGS_PAGE_SIZE = 50;

/**
 * An index rulebook reports only two buckets. Doubtful, unscreened and
 * coverage do not exist per rulebook (handover §12.4), so there are no
 * fields for them here.
 */
export interface EtfRulebookWeights {
  compliantWeightPerc: number | null;
  nonCompliantWeightPerc: number | null;
}

/** The full 32-key row: the screener's fields plus the per-rulebook split. */
export interface EtfDetail extends EtfRow {
  rulebookWeights: Record<EtfIndexRulebook, EtfRulebookWeights>;
  /** Naive date-time: when we last pulled the basket. */
  holdingsFetchedAt: string | null;
}

/** One constituent (11 keys upstream). */
export interface EtfHolding {
  holdingTicker: string | null;
  holdingName: string | null;
  /** Can be negative (a short leg). Never abs() it. */
  weightPerc: number | null;
  screened: boolean | null;
  /** A real company verdict (AAOIFI). null / ERROR_DATA are normal: not rated. */
  status: string | null;
  /** HALAL | NOT HALAL | INCOMPLETE_DATA | null on a holding. */
  statusDjim: string | null;
  statusFtse: string | null;
  statusMsci: string | null;
  statusSp: string | null;
}

export interface EtfHoldingsResponse {
  etfTicker: string | null;
  /** Naive date (YYYY-MM-DD), the provider's basket date. */
  asOf: string | null;
  holdings: EtfHolding[];
  total: number;
  limit: number;
  offset: number;
}
